import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink } from "react-router-dom";
import {
  Bell, Search, ChevronDown, Menu, X,
  AlertCircle, CheckCircle, Activity,
  Clock, MapPin, Minus, Plus, Languages, PlayCircle, Bot
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { positionInitials } from "../../auth/officers";
import { usePreferences, FONT_SCALES } from "../../context/PreferencesContext";
import { LIVE_DEMO, LIVE_DEMO_URL } from "./navConfig";

const ALERTS = [
  { id:1, type:"danger",  msg:{en:"Crowd surge at Ramkund Ghat — Entry 2",       mr:"रामकुंड घाट येथे गर्दीची लाट — प्रवेशद्वार 2"},        time:{en:"2 min ago",  mr:"2 मिनिटांपूर्वी"}  },
  { id:2, type:"warning", msg:{en:"Water Station 8 — 91% capacity, Panchvati",    mr:"जल केंद्र 8 — 91% क्षमता, पंचवटी"},                    time:{en:"6 min ago",  mr:"6 मिनिटांपूर्वी"}  },
  { id:3, type:"info",    msg:{en:"Shuttle SR-04 delayed 22 min — Trimbakeshwar", mr:"शटल SR-04, 22 मिनिटे विलंबित — त्र्यंबकेश्वर"},        time:{en:"11 min ago", mr:"11 मिनिटांपूर्वी"} },
  { id:4, type:"success", msg:{en:"Missing person MP-1182 resolved",              mr:"हरवलेली व्यक्ती MP-1182 सापडली"},                       time:{en:"18 min ago", mr:"18 मिनिटांपूर्वी"} },
];

const HEADER_COPY = {
  en: {
    logoName: "Kumbh AI",
    subtitle: "AI Command Assurance Platform · Nashik, Maharashtra",
    searchPlaceholder: "Search alerts, ghats, zones...",
    textSize: "Text size",
    notifTitle: "Command Alerts",
    notifNew: "4 new",
    viewAllAlerts: "View all alerts →",
    officerFallback: "Officer",
    menuItems: ["Profile Settings", "Access Logs", "Audit Trail", "Help & Support"],
    signOut: "Sign Out",
    statusTagline: "Simhastha Kumbh 2027",
    statusLocation: "Nashik & Trimbakeshwar, Maharashtra",
    statusOperational: "All 14 modules operational",
    statusSummary: "6,84,210 pilgrims active · 18 open incidents · AI confidence 93.7%",
  },
  mr: {
    logoName: "कुंभ एआय",
    subtitle: "एआय कमांड अ‍ॅश्युरन्स प्लॅटफॉर्म · नाशिक, महाराष्ट्र",
    searchPlaceholder: "अलर्ट, घाट, झोन शोधा...",
    textSize: "मजकूर आकार",
    notifTitle: "कमांड अलर्ट्स",
    notifNew: "4 नवीन",
    viewAllAlerts: "सर्व अलर्ट पहा →",
    officerFallback: "अधिकारी",
    menuItems: ["प्रोफाइल सेटिंग्ज", "अ‍ॅक्सेस लॉग्स", "ऑडिट ट्रेल", "मदत व सहाय्य"],
    signOut: "साइन आउट",
    statusTagline: "सिंहस्थ कुंभ 2027",
    statusLocation: "नाशिक व त्र्यंबकेश्वर, महाराष्ट्र",
    statusOperational: "सर्व 14 मॉड्यूल्स कार्यरत",
    statusSummary: "6,84,210 यात्रेकरू सक्रिय · 18 प्रलंबित घटना · एआय विश्वासार्हता 93.7%",
  },
};

const typeStyle = { danger:"text-red-600", warning:"text-amber-600", info:"text-blue-600", success:"text-emerald-600" };
const typeIcon  = {
  danger:  <AlertCircle size={13} />,
  warning: <AlertCircle size={13} />,
  info:    <Bell size={13} />,
  success: <CheckCircle size={13} />,
};
const typeBg = { danger:"bg-red-50", warning:"bg-amber-50", info:"bg-blue-50", success:"bg-emerald-50" };

export default function Header({ sidebarOpen, setSidebarOpen }) {
  const { user, logout }          = useAuth();
  const { lang, setLang, scaleIdx, increaseText, decreaseText } = usePreferences();
  const hc = HEADER_COPY[lang];
  const [time, setTime]           = useState(new Date());
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // "-u-nu-latn" pins the Unicode numbering system to Latin digits even in
  // the mr-IN locale — its CLDR default is Devanagari numerals (०-९), which
  // would render the clock as "०१:५२ अपराह्न" and mismatch every other
  // Latin-digit figure on the page. Government of Maharashtra practice is
  // Marathi words with Latin figures, so the numbering is pinned to match.
  const locale = lang === "mr" ? "mr-IN-u-nu-latn" : "en-IN";
  const fmtTime = d => d.toLocaleTimeString(locale, { hour:"2-digit", minute:"2-digit", second:"2-digit" });

  return (
    <>
      {/* ── Main header bar ── */}
      <header className="fixed top-0 left-0 right-0 z-50 gov-gradient-chrome border-b border-saffron-dark shadow-header">

        {/* Primary bar */}
        <div className="h-16 flex items-center px-4 gap-3">

          {/* Hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-xl hover:bg-white/15 transition-colors text-white"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {/* The kalash emblem from public/favicon.svg — the same mark the
                browser tab shows, so the tab and the console agree. It carries
                its own saffron disc, so it needs no white tile behind it. */}
            <img
              src="/favicon.svg"
              alt=""
              width={48}
              height={48}
              className="w-12 h-12 flex-shrink-0 drop-shadow-sm"
            />
            <div className="hidden sm:block">
              <div className="flex items-center gap-2">
                <span className="font-black text-[19px] text-white leading-none tracking-tight">
                  {hc.logoName}
                </span>

              </div>
              <div className="text-[11px] text-white/90 leading-none mt-1 hidden md:block">
                {hc.subtitle}
              </div>
            </div>
          </div>

          {/* LIVE OPS badge and the Feed ticker moved to the dashboard's
              white hero panel. On the saffron bar they competed with the
              wordmark for the same strip of space; on the panel they have
              room and sit beside the figures they describe. */}

          <div className="flex-1 xl:flex-none" />

          {/* Accessibility: text size + language — same preferences used on the public landing page */}

          {/* Kumbh Sathi — in-app route, so a NavLink rather than an anchor:
              an <a href> would hard-reload the SPA and drop the session,
              which for this app means being bounced back to Landing. */}
          <NavLink
            to="/sahayak"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 mr-1.5 rounded-full bg-white/15 border border-white/35 text-white text-[11px] font-black hover:bg-white/25 transition-colors flex-shrink-0"
          >
            <Bot size={14} />
            {lang === "mr" ? "कुंभ साथी" : "Kumbh Sathi"}
          </NavLink>

          {/* Live Demo — opens the standalone camera-analytics demo in a new tab.
              Mobile/tablet reach it from the drawer instead. */}
          <a
            href={LIVE_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 mr-1 rounded-full bg-white text-saffron-dark text-[11px] font-black shadow-sm hover:bg-white/90 transition-colors flex-shrink-0"
          >
            <PlayCircle size={14} />
            {LIVE_DEMO[lang]}
          </a>

          {/* Clock — time only. The date moved to the dashboard's white hero
              panel, where it sits on a light surface and has room to breathe
              instead of crowding the bar. */}
          <div className="hidden lg:flex items-center mr-1">
            <span className="text-base font-black text-white tabular-nums leading-none">{fmtTime(time)}</span>
          </div>

          {/* Search */}
          <div className="hidden md:flex items-center gap-2 bg-white/15 border border-white/30 rounded-xl px-3 py-2 w-52 focus-within:ring-2 focus-within:ring-white/30 focus-within:border-white/50 transition-all">
            <Search size={13} className="text-white/90 flex-shrink-0" />
            <input
              type="text"
              placeholder={hc.searchPlaceholder}
              className="bg-transparent text-sm text-white placeholder-white/60 outline-none w-full"
            />
          </div>

          {/* Mobile search toggle (search box itself is md:flex and hidden below md) */}
          <div className="relative md:hidden">
            <button
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              className="p-2 rounded-xl hover:bg-white/15 transition-colors text-white"
            >
              {mobileSearchOpen ? <X size={18} /> : <Search size={18} />}
            </button>
            <AnimatePresence>
              {mobileSearchOpen && (
                <motion.div
                  initial={{ opacity:0, y:6, scale:0.97 }}
                  animate={{ opacity:1, y:0, scale:1 }}
                  exit={{ opacity:0, y:6, scale:0.97 }}
                  className="absolute right-0 top-12 w-[calc(100vw-2rem)] max-w-xs bg-white rounded-2xl shadow-xl border border-slate-200 z-50 p-2"
                >
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                    <Search size={14} className="text-slate-400 flex-shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      placeholder={hc.searchPlaceholder}
                      className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none w-full"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
              className="relative p-2 rounded-xl hover:bg-white/15 transition-colors"
            >
              <Bell size={18} className="text-white" />
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] font-black text-white flex items-center justify-center">4</span>
            </button>

            <AnimatePresence>
              {notifOpen && (
                <motion.div
                  initial={{ opacity:0, y:6, scale:0.97 }}
                  animate={{ opacity:1, y:0, scale:1 }}
                  exit={{ opacity:0, y:6, scale:0.97 }}
                  className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <span className="font-black text-sm text-slate-900">{hc.notifTitle}</span>
                    <span className="badge badge-critical text-[10px]">{hc.notifNew}</span>
                  </div>
                  <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                    {ALERTS.map(n => (
                      <div key={n.id} className={`px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${typeBg[n.type]}`}>
                        <div className="flex items-start gap-3">
                          <span className={`mt-0.5 ${typeStyle[n.type]}`}>{typeIcon[n.type]}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800 leading-snug">{n.msg[lang]}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><Clock size={9} />{n.time[lang]}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2.5 border-t border-slate-100">
                    <button className="text-xs font-black text-blue-700 hover:text-blue-800 w-full text-center">{hc.viewAllAlerts}</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Text size and language sit beside the profile control: they are
              all "who is using this and how", not operational data, so they
              belong together at the end of the bar rather than split across
              it. Sized down to sit comfortably next to the avatar. */}
          <div className="hidden lg:flex items-center gap-1.5 text-[10px] font-semibold mr-1">
            <div className="flex items-center border border-white/30 rounded-md overflow-hidden">
              <button
                type="button"
                aria-label="Decrease text size"
                onClick={decreaseText}
                disabled={scaleIdx === 0}
                className="w-5 h-5 flex items-center justify-center text-white hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <Minus size={10} />
              </button>
              <span className="w-5 h-5 flex items-center justify-center border-x border-white/30 text-white">A</span>
              <button
                type="button"
                aria-label="Increase text size"
                onClick={increaseText}
                disabled={scaleIdx === FONT_SCALES.length - 1}
                className="w-5 h-5 flex items-center justify-center text-white hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <Plus size={10} />
              </button>
            </div>

            <div className="flex items-center border border-white/30 rounded-md overflow-hidden">
              <Languages size={10} className="mx-1.5 text-white/90" />
              <button
                type="button"
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}
                className={`px-1.5 h-5 transition-colors ${lang === "en" ? "bg-white text-saffron-dark" : "text-white/90 hover:bg-white/15"}`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLang("mr")}
                aria-pressed={lang === "mr"}
                className={`px-1.5 h-5 transition-colors ${lang === "mr" ? "bg-white text-saffron-dark" : "text-white/90 hover:bg-white/15"}`}
              >
                मराठी
              </button>
            </div>
          </div>

          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}
              className="flex items-center gap-1.5 p-1 pr-1.5 rounded-lg hover:bg-white/15 transition-colors"
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[10px] font-black border border-white/40"
                style={{ background: user ? user.accent[0] : "#E3A322" }}
              >
                {positionInitials(user?.position || "")}
              </div>
              {/* Only the role line is kept inline. The level/access detail
                  moved into the dropdown — it was the widest thing in the
                  bar and it is reference information, not something an
                  operator reads at a glance. */}
              <div className="hidden xl:block text-left max-w-[120px]">
                <div className="text-[11px] font-bold text-white leading-none truncate">
                  {user?.position || hc.officerFallback}
                </div>
              </div>
              <ChevronDown size={11} className="text-white/90 hidden xl:block" />
            </button>

            <AnimatePresence>
              {profileOpen && (
                <motion.div
                  initial={{ opacity:0, y:6, scale:0.97 }}
                  animate={{ opacity:1, y:0, scale:1 }}
                  exit={{ opacity:0, y:6, scale:0.97 }}
                  className="absolute right-0 top-12 w-64 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden"
                >
                  <div className="px-4 py-4 border-b border-slate-100 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 gov-gradient rounded-xl flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                        style={user ? { background: `linear-gradient(135deg, ${user.accent[0]}, ${user.accent[1]})` } : undefined}
                      >
                        {positionInitials(user?.position || "")}
                      </div>
                      <div className="min-w-0">
                        <div className="font-black text-sm text-slate-900 truncate">{user?.position || hc.officerFallback}</div>
                        <div className="text-[10px] text-slate-400 truncate">{user?.email || ""}</div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-blue-100 text-blue-800 mt-1">
                          {user ? `${lang === "mr" ? "स्तर" : "Level"} ${user.level.replace(/^L/, "")} · ${user.access}` : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="py-1">
                    {hc.menuItems.map(item => (
                      <button key={item} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        {item}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-slate-100">
                    <button
                      onClick={() => { setProfileOpen(false); logout(); }}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                    >
                      {hc.signOut}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Status bar */}
        <div className="hidden lg:flex items-center gap-4 px-4 py-1.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500">
          <div className="flex items-center gap-1.5">
            <MapPin size={9} className="text-blue-600" />
            <span className="font-semibold text-slate-600">{hc.statusTagline}</span>
            <span>·</span>
            <span>{hc.statusLocation}</span>
          </div>
          <div className="h-3 w-px bg-slate-300" />
          <div className="flex items-center gap-1.5">
            <Activity size={9} className="text-emerald-600" />
            <span className="font-semibold text-emerald-600">{hc.statusOperational}</span>
          </div>
          <div className="h-3 w-px bg-slate-300" />
          <span>{hc.statusSummary}</span>
        </div>
      </header>
    </>
  );
}
